'use client'

import { useEffect, useRef, useState } from 'react'
import { FrostyPill } from './frosty-pill'

type Widget = typeof import('./frosty-chat-widget').ChatWidget
const loadWidget = () => import('./frosty-chat-widget').then(module => module.ChatWidget)

/** Closed chat needs a launcher, not the conversation and recommendation graph. */
export function LazyFrosty({ load = loadWidget }: { load?: () => Promise<Widget> }) {
  const [Widget, setWidget] = useState<Widget | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const pending = useRef(false)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const activate = async () => {
    if (pending.current) return
    pending.current = true
    setLoading(true)
    setFailed(false)
    try {
      const loaded = await load()
      if (mounted.current) setWidget(() => loaded)
    } catch {
      if (mounted.current) setFailed(true)
    } finally {
      pending.current = false
      if (mounted.current) setLoading(false)
    }
  }

  // Keep the loaded widget mounted: closing it must retain turns and tab quota.
  if (Widget) return <Widget initialOpen />
  return (
    <>
      <FrostyPill open={false} onToggle={() => void activate()} busy={loading} label={failed ? 'Ask Frosty again' : 'Ask Frosty'} descriptionId={failed ? 'frosty-load-error' : undefined} />
      {failed ? <span id="frosty-load-error" role="status" className="sr-only">Chat could not load. Select Ask Frosty again to retry.</span> : null}
    </>
  )
}
